// Reviews service for handling Google My Business data
import { db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';

// Transform webhook data to match the Reviews page structure
export const transformReviewsData = (webhookData) => {
  // Parse numeric values
  const averageRating = parseFloat(webhookData.averageRating) || 0;
  const totalReviews = parseInt(webhookData.totalReviews) || 0;
  const totalPhotos = parseInt(webhookData.totalPhotos) || 0;
  const totalTopics = parseInt(webhookData.totalTopics) || 0;
  const topicReviews = parseInt(webhookData.topicReviews) || 0;
  const attributes = parseInt(webhookData.attributes) || 0;

  // Parse star distribution
  const fiveStars = parseInt(webhookData.fiveStars) || 0;
  const fourStars = parseInt(webhookData.fourStars) || 0;
  const threeStars = parseInt(webhookData.threeStars) || 0;
  const twoStars = parseInt(webhookData.twoStars) || 0;
  const oneStars = parseInt(webhookData.oneStars) || 0;

  // Calculate percentages for rating distribution
  const totalRatingCount = fiveStars + fourStars + threeStars + twoStars + oneStars;
  const calculatePercentage = (count) => totalRatingCount > 0 ? Math.round((count / totalRatingCount) * 100) : 0;

  // Build categories array
  const categories = [
    webhookData.firstCategory,
    webhookData.secondCategory,
    webhookData.thirdCategory,
    webhookData.fourthCategory,
    webhookData.fifthCategory
  ].filter(Boolean).map((name, index) => ({ id: (index + 1).toString(), name }));

  // Build additional IDs array
  const additionalIds = [
    webhookData.firstAdditional,
    webhookData.secondAdditional,
    webhookData.thirdAdditional,
    webhookData.fourthAdditional,
    webhookData.fifthAdditional
  ].filter(Boolean).map((name, index) => ({ id: (index + 1).toString(), name }));

  // Build competitors array
  const competitors = [
    webhookData.firstCompetitorName,
    webhookData.secondCompetitorName,
    webhookData.thirdCompetitorName,
    webhookData.fourthCompetitorName,
    webhookData.fifthCompetitorName
  ].filter(Boolean).map((name, index) => ({
    name,
    averageRating: 0, // These would need to be fetched separately
    totalReviews: 0   // These would need to be fetched separately
  }));

  // Check if value exists (not empty, null, or undefined)
  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    return Boolean(value);
  };

  return {
    categoryIds: categories,
    additionalIds: additionalIds,
    totalPhotos,
    totalReviews,
    newThisMonth: 0, // This would need to be calculated from historical data
    averageRating,
    ratingDistribution: [
      { stars: 5, count: fiveStars, percentage: calculatePercentage(fiveStars) },
      { stars: 4, count: fourStars, percentage: calculatePercentage(fourStars) },
      { stars: 3, count: threeStars, percentage: calculatePercentage(threeStars) },
      { stars: 2, count: twoStars, percentage: calculatePercentage(twoStars) },
      { stars: 1, count: oneStars, percentage: calculatePercentage(oneStars) }
    ],
    fiveStarReviews: fiveStars,
    competitors,
    businessMetrics: {
      totalTopics,
      topicReviews,
      address: hasValue(webhookData.address),
      phone: hasValue(webhookData.phone),
      website: hasValue(webhookData.website),
      logo: hasValue(webhookData.logo),
      hours: hasValue(webhookData.hours),
      claimed: hasValue(webhookData.claimed),
      attributes
    }
  };
};

// Save reviews data to Firestore
export const saveReviewsData = async (userId, webhookData) => {
  try {
    const transformedData = transformReviewsData(webhookData);
    
    // Save to user's reviews collection
    const reviewsRef = doc(db, 'users', userId, 'reviews', 'googleMyBusiness');
    await setDoc(reviewsRef, {
      ...transformedData,
      lastUpdated: new Date().toISOString(),
      source: 'webhook'
    });

    console.log('✅ Reviews data saved successfully');
    return { success: true, data: transformedData };
  } catch (error) {
    console.error('❌ Error saving reviews data:', error);
    throw error;
  }
};

// Get reviews data from Firestore
export const getReviewsData = async (userId) => {
  try {
    const reviewsRef = doc(db, 'users', userId, 'reviews', 'googleMyBusiness');
    const docSnap = await getDoc(reviewsRef);
    
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: false, data: null };
    }
  } catch (error) {
    console.error('❌ Error getting reviews data:', error);
    throw error;
  }
};

// Webhook endpoint handler (for server-side use)
export const handleReviewsWebhook = async (req, res) => {
  try {
    const { accountId, userID, ...webhookData } = req.body;
    
    if (!accountId || !userID) {
      return res.status(400).json({ error: 'Missing accountId or userID' });
    }

    // Save the data
    await saveReviewsData(userID, { accountId, userID, ...webhookData });
    
    res.status(200).json({ success: true, message: 'Reviews data saved successfully' });
  } catch (error) {
    console.error('❌ Webhook error:', error);
    res.status(500).json({ error: 'Internal server error' });
  }
};
